import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { LOGO_BASE_URL, GALLERY_BASE_URL } from '../../config';
import { buildMapsUrls } from '../../utils/directions';
import '../../styles/BusinessProfile.css';

function toExternalUrl(url) {
  if (!url || !String(url).trim()) return null;
  const trimmed = String(url).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const SOCIAL_LINKS = [
  {
    key: 'facebook',
    label: 'فيسبوك',
    className: 'profile-social-facebook',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    key: 'instagram',
    label: 'إنستغرام',
    className: 'profile-social-instagram',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    key: 'tiktok',
    label: 'تيك توك',
    className: 'profile-social-tiktok',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.77 1.52V6.76a4.85 4.85 0 01-1.01-.07z" />
      </svg>
    ),
  },
];

function BusinessProfile({ account, onShare }) {
  const logoUrl = `${LOGO_BASE_URL}/${account.id}`;
  const mapsUrls = buildMapsUrls(account.address);
  const [descExpanded, setDescExpanded] = useState(false);

  const sortedPhotos = [...(account.photos || [])].sort((a, b) => a.sort_order - b.sort_order);
  const heroPhoto = sortedPhotos[0];
  const heroUrl = heroPhoto ? `${GALLERY_BASE_URL}/${account.id}/${heroPhoto.id}` : null;
  const longDesc = (account.description || '').length > 150;

  const handleShare = async () => {
    if (onShare) {
      onShare();
      return;
    }
    const url = `${window.location.origin}/?businessid=${encodeURIComponent(account.id)}`;
    const shareData = {
      title: account.title || 'ادواري',
      text: `احجز موعدك في ${account.title || 'الصالون'} عبر ادواري`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('تم نسخ الرابط');
    }
  };

  const socialLinks = SOCIAL_LINKS.map(({ key, label, className, icon }) => {
    const href = toExternalUrl(account[key]);
    if (!href) return null;
    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`profile-social-link ${className}`}
        aria-label={label}
        title={label}
      >
        {icon}
      </a>
    );
  }).filter(Boolean);

  return (
    <section className="business-profile">
      <div className={`business-profile-hero${!heroUrl ? ' business-profile-hero--fallback' : ''}`}>
        {heroUrl && (
          <img
            className="business-profile-hero-img"
            src={heroUrl}
            alt=""
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.closest('.business-profile-hero').classList.add('business-profile-hero--fallback');
            }}
          />
        )}
        <div className="business-profile-hero-overlay">
          <div className="business-profile-hero-identity">
            <div className="business-profile-logo">
              <img
                src={logoUrl}
                alt={account.title}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.business-profile-logo-fallback');
                  if (fallback) fallback.classList.add('visible');
                }}
              />
              <span className="business-profile-logo-fallback">{account.title?.charAt(0) || '?'}</span>
            </div>
            <div className="business-profile-hero-text">
              <h1 className="business-profile-title">{account.title}</h1>
              {account.address && (
                <p className="business-profile-hero-address">{account.address}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="business-profile-strip">
        <div className="business-profile-chips">
          <span className="profile-chip">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {account.address || 'العنوان'}
          </span>
          {account.phone && (
            <a href={`tel:${account.phone}`} className="profile-chip profile-chip-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              {account.phone}
            </a>
          )}
          {account.whatsapp && (
            <a
              href={`https://wa.me/${account.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-chip profile-chip-link profile-chip-whatsapp"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              واتساب
            </a>
          )}
          <button type="button" className="profile-chip profile-chip-btn" onClick={handleShare}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            مشاركة
          </button>
          {mapsUrls && (
            <a
              href={mapsUrls.waze}
              target="_blank"
              rel="noopener noreferrer"
              className="profile-chip profile-chip-link profile-chip-waze"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" />
              </svg>
              Waze
            </a>
          )}
        </div>

        {socialLinks.length > 0 && (
          <div className="business-profile-social">
            {socialLinks}
          </div>
        )}

        {account.description && (
          <div className="business-profile-desc-wrap">
            <p className={`business-profile-description${descExpanded ? ' expanded' : ''}`}>
              {account.description}
            </p>
            {longDesc && (
              <button
                type="button"
                className="business-profile-desc-toggle"
                onClick={() => setDescExpanded((prev) => !prev)}
              >
                {descExpanded ? 'أخفِ' : 'اقرأ المزيد'}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default BusinessProfile;
